import {
  Team,
  User,
  UserTeam,
  getTreeTeams as hierarchizeTeams,
  isNilOrEmpty,
  resolveOneToMany,
  resolveOneToOne,
  todayDateTime,
} from '@rentguru/commons-utils';
import { isUnauthorizedGraphQLError, recordWasUpdated } from '@up2rent/graphql';
import React, { useContext, useEffect, useMemo } from 'react';

import { createTeamApi, deleteTeamApi, fetchTeamsAndMemberships, toTeam, updateTeamApi } from '../auth/teamRoleApi';

import { ContextLoaderAction, ContextLoaderStore, useContextLoader } from './ContextLoader';
import { useUser } from './UserContext';
import { FETCH_RETRY_DELAY_MS, UsersContext, useUsers } from './UsersContext';

export type ActionTeam =
  | {
      type: 'SHOULD_FETCH_TEAM' | 'IS_FETCHING_TEAM';
    }
  | {
      type: 'ADD_TEAM';
      payload: { team: Team };
    }
  | {
      type: 'FETCHED_TEAM';
      payload: { teams: Team[] };
    }
  | {
      type: 'UPDATE_TEAM';
      payload: { team: Team };
    }
  | {
      type: 'DELETE_TEAM';
      payload: { id: string };
    };
export interface TeamsContext {
  loading: boolean;
  error: string | null;
  teams: Team[];
  getTeam: (id: string) => Team | undefined;
  getTreeTeams: () => Team[];
  createTeam: (input: Omit<Team, 'id' | 'clientId' | 'readId'>) => Promise<Team>;
  updateTeam: (original: Team, updates: Partial<Team>) => Promise<Team>;
  deleteTeam: (id: string) => Promise<void>;
  getUserTeams: (userId: string) => Team[];
  getTeamUsers: (teamId: string) => User[];
  updateUserTeam: UsersContext['updateUserTeam'];
  deleteUserTeam: UsersContext['deleteUserTeam'];
  setFetchTeam: () => void;
}

export const teamReducerDelegation = (state: ContextLoaderStore, action: ContextLoaderAction): ContextLoaderStore => {
  switch (action.type) {
    case 'SHOULD_FETCH_TEAM':
      if (state.Team.loading || state.Team.shouldFetch) {
        return state;
      }
      return {
        ...state,
        Team: { ...state.Team, shouldFetch: true },
      };
    case 'IS_FETCHING_TEAM':
      if (state.Team.loading) {
        return state;
      }
      return {
        ...state,
        Team: { ...state.Team, loading: true },
      };
    case 'FETCHED_TEAM':
      return {
        ...state,
        Team: {
          ...state.Team,
          data: action.payload.teams,
          loading: false,
          shouldFetch: false,
          lastFetch: todayDateTime(),
        },
      };
    case 'ADD_TEAM':
      // Check if already present - If already added by this user or coming from another user
      if (state.Team.data?.find((object) => object.id === action.payload.team.id)) {
        return state;
      }

      return {
        ...state,
        Team: {
          ...state.Team,
          data: [...state.Team.data, action.payload.team],
        },
      };
    case 'UPDATE_TEAM':
      // No data
      if (isNilOrEmpty(state.Team.data)) {
        return state;
      }

      // Already present and same object
      const currentObject = state.Team.data?.find((object) => object.id === action.payload.team.id);
      if (!currentObject) {
        return {
          ...state,
          Team: {
            ...state.Team,
            data: [...state.Team.data, action.payload.team],
          },
        };
      }
      if (!recordWasUpdated(currentObject, action.payload.team)) {
        return state;
      }

      // Update
      return {
        ...state,
        Team: {
          ...state.Team,
          data: state.Team.data.map((object) => {
            if (object.id === action.payload.team.id) {
              return action.payload.team;
            }
            return object;
          }),
        },
      };
    case 'DELETE_TEAM':
      if (isNilOrEmpty(state.Team.data)) {
        return state;
      }
      return {
        ...state,
        Team: {
          ...state.Team,
          data: state.Team.data.filter((object) => object.id !== action.payload.id),
        },
      };

    default:
      return state;
  }
};

const fetchTeams = async (clientId: string): Promise<Team[]> => {
  const { teams } = await fetchTeamsAndMemberships();
  return teams.map((team) => toTeam(team, clientId));
};

// eslint-disable-next-line no-redeclare
export const TeamsContext = React.createContext<TeamsContext | null>(null);

export const TeamsContextProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const {
    User: { data: usersLoader },
    Team: { data: teamsLoader, loading: teamLoading, shouldFetch },
    UserTeam: { data: userTeamsLoader, loading: userTeamsLoading },
    dispatch: contextDispatch,
  } = useContextLoader();
  const { clientId } = useUser();
  const { updateUserTeam, deleteUserTeam } = useUsers();
  const loading = teamLoading || userTeamsLoading;

  const setFetchTeam = () => {
    contextDispatch({ type: 'SHOULD_FETCH_TEAM' });
  };

  const { teams, userTeams } = useMemo<{ teams: Team[]; userTeams: UserTeam[] }>(() => {
    if (loading) {
      return { teams: [], userTeams: [] };
    }

    const userTeamsReduced = userTeamsLoader.reduce((acc: UserTeam[], userTeam: UserTeam) => {
      acc.push({
        ...userTeam,
        user: resolveOneToOne(userTeam.userId, usersLoader, 'id'),
        team: resolveOneToOne(userTeam.teamId, teamsLoader, 'id'),
      });
      return acc;
    }, []);

    const teamsReduced = teamsLoader.reduce((acc: Team[], team: Team) => {
      acc.push({
        ...team,
        users: resolveOneToMany(team.id, userTeamsReduced, 'teamId'),
      });
      return acc;
    }, []);

    return { teams: teamsReduced, userTeams: userTeamsReduced };
  }, [loading, usersLoader, teamsLoader, userTeamsLoader]);

  useEffect(() => {
    const fetchAndSet = async () => {
      contextDispatch({ type: 'IS_FETCHING_TEAM' });
      try {
        const result = await fetchTeams(clientId!);
        contextDispatch({ type: 'FETCHED_TEAM', payload: { teams: result } });
      } catch (error) {
        // Always resolve the loading flag, otherwise ContextLoader shows an infinite LoadingScreen.
        console.error(error);
        contextDispatch({ type: 'FETCHED_TEAM', payload: { teams: [] } });
        if (!isUnauthorizedGraphQLError(error)) {
          // Transient failure: schedule a refetch instead of caching emptiness as valid data.
          setTimeout(() => contextDispatch({ type: 'SHOULD_FETCH_TEAM' }), FETCH_RETRY_DELAY_MS);
        }
      }
    };
    if (shouldFetch) fetchAndSet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetch]);

  const createTeam = async (input: Omit<Team, 'id' | 'clientId' | 'readId'>) => {
    const { team } = await createTeamApi({ name: input.name, parentId: input.parentId ?? null });
    const created = toTeam(team, clientId!);
    contextDispatch({ type: 'ADD_TEAM', payload: { team: created } });
    return created;
  };

  const updateTeam = async (original: Team, updates: Partial<Team>) => {
    await updateTeamApi({
      id: original.id,
      ...(updates.name !== undefined ? { name: updates.name } : {}),
      ...(updates.parentId !== undefined ? { parentId: updates.parentId } : {}),
    });
    const updated: Team = { ...original, ...updates, _version: (original._version ?? 0) + 1 };
    contextDispatch({ type: 'UPDATE_TEAM', payload: { team: updated } });
    return updated;
  };

  const deleteTeam = async (id: string) => {
    await deleteTeamApi(id);
    contextDispatch({ type: 'DELETE_TEAM', payload: { id } });
  };

  const getTeam = (id: string): Team | undefined => {
    if (loading) {
      return undefined;
    }
    return teams.find((t) => t.id === id);
  };

  const getTreeTeams = () => {
    if (loading) {
      return [];
    }
    return hierarchizeTeams(teams);
  };

  const getUserTeams = (userId: string): Team[] =>
    userTeams.reduce((acc: Team[], userTeam: UserTeam) => {
      if (userTeam.userId === userId) acc.push(userTeam.team!);
      return acc;
    }, []);

  const getTeamUsers = (teamId: string): User[] =>
    userTeams.reduce((acc: User[], userTeam: UserTeam) => {
      if (userTeam.teamId === teamId) acc.push(userTeam.user!);
      return acc;
    }, []);

  const values = useMemo(
    () => ({
      teams,
      error: null,
      loading,
      getTeam,
      getTreeTeams,
      createTeam,
      updateTeam,
      deleteTeam,
      getUserTeams,
      getTeamUsers,
      updateUserTeam,
      deleteUserTeam,
      setFetchTeam,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [teams, loading]
  );

  return <TeamsContext.Provider value={values}>{children}</TeamsContext.Provider>;
};

export const useTeams = (): TeamsContext => {
  const context = useContext<TeamsContext | null>(TeamsContext);

  if (context === undefined) {
    throw new Error('`useTeams` hook must be used within a `TeamsContextProvider` component');
  }
  return context as TeamsContext;
};
